/**
 * Utility functions for filtering training sessions
 */

/**
 * Normalize user data to ensure consistent structure for filtering
 * @param {Object} user - User object from API
 * @returns {Object} - Normalized user object
 */
const normalizeUserData = (user) => {
  console.log('=== NORMALIZE USER DATA DEBUG ===');
  console.log('Raw user data:', user);

  if (!user) {
    console.log('No user data provided');
    return null;
  }

  const normalized = {
    id: user.id,
    is_admin: user.is_admin,
    crew_memberships: [],
    training_group_memberships: [],
  };

  // Handle crew memberships - could be direct array or nested objects
  console.log('Processing crew memberships:', user.crew_memberships);
  if (user.crew_memberships) {
    user.crew_memberships.forEach((membership) => {
      console.log('Processing crew membership:', membership);
      if (typeof membership === 'object') {
        if (membership.crew_id) {
          const normalizedMembership = {
            crew_id: parseInt(membership.crew_id),
            crew: membership.crew ? { id: parseInt(membership.crew.id) } : null,
          };
          normalized.crew_memberships.push(normalizedMembership);
          console.log('Added normalized crew membership (crew_id):', normalizedMembership);
        } else if (membership.crew?.id) {
          const normalizedMembership = {
            crew_id: parseInt(membership.crew.id),
            crew: { id: parseInt(membership.crew.id) },
          };
          normalized.crew_memberships.push(normalizedMembership);
          console.log('Added normalized crew membership (crew.id):', normalizedMembership);
        } else {
          console.log('Skipped crew membership (no crew_id or crew.id):', membership);
        }
      } else {
        console.log('Skipped crew membership (not an object):', membership);
      }
    });
  }

  // Handle training group memberships
  console.log('Processing training group memberships:', user.training_group_memberships);
  if (user.training_group_memberships) {
    user.training_group_memberships.forEach((membership) => {
      console.log('Processing training group membership:', membership);
      if (typeof membership === 'object' && membership.training_group_id) {
        const normalizedMembership = {
          training_group_id: parseInt(membership.training_group_id),
          role_in_group: membership.role_in_group,
        };
        normalized.training_group_memberships.push(normalizedMembership);
        console.log('Added normalized training group membership:', normalizedMembership);
      } else {
        console.log('Skipped training group membership (invalid format):', membership);
      }
    });
  }

  console.log('Final normalized user data:', normalized);
  console.log('=== END NORMALIZE USER DATA DEBUG ===');
  return normalized;
};

/**
 * Filter sessions based on crew ID
 * @param {Array} sessions - Array of training sessions
 * @param {number} crewId - Crew ID to filter by
 * @returns {Array} - Filtered sessions
 */
export const filterByCrew = (sessions, crewId) => {
  if (!crewId) return sessions;

  return sessions.filter((session) => {
    // Check if session has crew assignments
    if (!session.crew_assignments || session.crew_assignments.length === 0) {
      return false;
    }

    // Check if any crew assignment matches the crew ID
    return session.crew_assignments.some(
      (assignment) =>
        assignment.crew_id === parseInt(crewId) || assignment.crew?.id === parseInt(crewId)
    );
  });
};

/**
 * Filter sessions based on training group ID
 * @param {Array} sessions - Array of training sessions
 * @param {number} trainingGroupId - Training group ID to filter by
 * @returns {Array} - Filtered sessions
 */
export const filterByTrainingGroup = (sessions, trainingGroupId) => {
  if (!trainingGroupId) return sessions;

  return sessions.filter((session) => {
    // Check if session has crew assignments
    if (!session.crew_assignments || session.crew_assignments.length === 0) {
      return false;
    }

    // Check if any crew belongs to the specified training group
    return session.crew_assignments.some(
      (assignment) => assignment.crew?.training_group_id === parseInt(trainingGroupId)
    );
  });
};

/**
 * Filter sessions based on user participation
 * @param {Array} sessions - Array of training sessions
 * @param {string} userParticipation - Type of participation to filter by
 * @param {Object} user - Current user object
 * @returns {Array} - Filtered sessions
 */
export const filterByUserParticipation = (sessions, userParticipation, user) => {
  console.log('=== USER PARTICIPATION FILTER DEBUG ===');
  console.log('Filter type:', userParticipation);
  console.log('User:', user);
  console.log('Sessions count:', sessions.length);

  if (!userParticipation || !user) {
    console.log('No participation filter or user, returning all sessions');
    return sessions;
  }

  const normalizedUser = normalizeUserData(user);
  if (!normalizedUser) {
    console.log('Failed to normalize user data, returning all sessions');
    return sessions;
  }

  const userId = normalizedUser.id;
  const userCrewIds = normalizedUser.crew_memberships.map((m) => m.crew_id);
  const userTrainingGroupIds = normalizedUser.training_group_memberships.map(
    (m) => m.training_group_id
  );

  console.log('Extracted IDs:');
  console.log('- User ID:', userId);
  console.log('- User Crew IDs:', userCrewIds);
  console.log('- User Training Group IDs:', userTrainingGroupIds);

  // If user has no memberships and is not admin, return empty array for participation filters
  if (userCrewIds.length === 0 && userTrainingGroupIds.length === 0 && !user.is_admin) {
    console.log('User has no memberships and is not admin, returning empty array');
    return [];
  }

  const filteredSessions = sessions.filter((session) => {
    console.log('Checking session:', session.id, session.title);

    switch (userParticipation) {
      case 'assigned':
        // Sessions where user is directly assigned
        const hasUserAssignment = session.user_assignments?.some(
          (assignment) => parseInt(assignment.user_id) === parseInt(userId)
        );
        console.log('Session has user assignment:', hasUserAssignment);
        return hasUserAssignment;

      case 'crew':
        // Sessions where user's crews are assigned
        if (!session.crew_assignments || session.crew_assignments.length === 0) {
          console.log('Session has no crew assignments');
          return false;
        }
        const hasCrewAssignment = session.crew_assignments.some((assignment) => {
          const crewId = parseInt(assignment.crew_id);
          const matches = userCrewIds.includes(crewId);
          console.log(`Crew assignment ${crewId} matches user crews:`, matches);
          return matches;
        });
        console.log('Session has crew assignment:', hasCrewAssignment);
        return hasCrewAssignment;

      case 'training_group':
        // Sessions in user's training groups
        if (!session.crew_assignments || session.crew_assignments.length === 0) {
          console.log('Session has no crew assignments');
          return false;
        }
        const hasTrainingGroupAssignment = session.crew_assignments.some((assignment) => {
          const trainingGroupId = assignment.crew?.training_group_id;
          if (!trainingGroupId) {
            console.log('Crew has no training group');
            return false;
          }
          const matches = userTrainingGroupIds.includes(parseInt(trainingGroupId));
          console.log(`Training group ${trainingGroupId} matches user groups:`, matches);
          return matches;
        });
        console.log('Session has training group assignment:', hasTrainingGroupAssignment);
        return hasTrainingGroupAssignment;

      default:
        console.log('Unknown participation type, returning true');
        return true;
    }
  });

  console.log('Filtered sessions count:', filteredSessions.length);
  console.log('=== END USER PARTICIPATION FILTER DEBUG ===');
  return filteredSessions;
};

/**
 * Filter sessions based on date range
 * @param {Array} sessions - Array of training sessions
 * @param {string} dateRange - Date range to filter by ('upcoming', 'past', 'all')
 * @returns {Array} - Filtered sessions
 */
export const filterByDateRange = (sessions, dateRange) => {
  if (!dateRange || dateRange === 'all') return sessions;

  const today = new Date();
  today.setHours(0, 0, 0, 0); // Start of today

  return sessions.filter((session) => {
    if (!session.date) return false;

    const sessionDate = new Date(session.date);
    sessionDate.setHours(0, 0, 0, 0);

    switch (dateRange) {
      case 'upcoming':
        // Include today and future dates
        return sessionDate >= today;

      case 'past':
        // Include only past dates (before today)
        return sessionDate < today;

      default:
        return true;
    }
  });
};

/**
 * Apply all filters to sessions
 * @param {Array} sessions - Array of training sessions
 * @param {Object} filters - Filter object with crewId, trainingGroupId, userParticipation, dateRange
 * @param {Object} user - Current user object
 * @returns {Array} - Filtered sessions
 */
export const applySessionFilters = (sessions, filters, user) => {
  let filteredSessions = [...sessions];

  // Apply crew filter
  filteredSessions = filterByCrew(filteredSessions, filters.crewId);

  // Apply training group filter
  filteredSessions = filterByTrainingGroup(filteredSessions, filters.trainingGroupId);

  // Apply user participation filter
  filteredSessions = filterByUserParticipation(filteredSessions, filters.userParticipation, user);

  // Apply date range filter
  filteredSessions = filterByDateRange(filteredSessions, filters.dateRange);

  return filteredSessions;
};

/**
 * Get filter summary for display
 * @param {Object} filters - Current filters
 * @returns {string} - Human-readable filter summary
 */
export const getFilterSummary = (filters) => {
  const activeFilters = [];

  if (filters.crewId) activeFilters.push('Crew');
  if (filters.trainingGroupId) activeFilters.push('Training Group');
  if (filters.userParticipation) activeFilters.push('My Participation');
  if (filters.dateRange && filters.dateRange !== 'upcoming') activeFilters.push('Date Range');

  if (activeFilters.length === 0) return 'No filters applied';
  if (activeFilters.length === 1) return `Filtered by ${activeFilters[0]}`;

  return `Filtered by ${activeFilters.slice(0, -1).join(', ')} and ${activeFilters[activeFilters.length - 1]}`;
};
